import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Search, Upload, Plus, Pencil, Trash2, BookMarked, Loader2, FileSpreadsheet,
  CheckCircle2, AlertCircle, MapPin, X,
} from 'lucide-react';
import { toast } from 'sonner';

const QuickCoursesManager = ({ api }) => {
  const [universities, setUniversities] = useState([]);
  const [selectedUni, setSelectedUni] = useState('');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editing, setEditing] = useState(null);    // course being edited
  const [creating, setCreating] = useState(false); // create modal open
  const [deleteId, setDeleteId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api().get('/master-data?type=university');
        setUniversities(r.data || []);
      } catch (_e) { setUniversities([]); }
    })();
  }, [api]);

  const fetchCourses = useCallback(async () => {
    if (!selectedUni) { setCourses([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ university_id: selectedUni });
      if (search) params.set('q', search);
      const r = await api().get(`/quick-courses?${params.toString()}`);
      setCourses(r.data?.courses || []);
    } catch (_e) {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [api, selectedUni, search]);

  // Debounced re-fetch when university or search changes
  useEffect(() => {
    const t = setTimeout(fetchCourses, 250);
    return () => clearTimeout(t);
  }, [fetchCourses]);

  const handleImport = async (file) => {
    if (!selectedUni) { toast.error('Pick a university first'); return; }
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('university_id', selectedUni);
      form.append('file', file);
      const r = await api().post('/quick-courses/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      toast.success(`Imported: ${r.data.created} new + ${r.data.updated} updated`);
      fetchCourses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api().delete(`/quick-courses/${deleteId}`);
      toast.success('Course deleted');
      setDeleteId(null);
      fetchCourses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const selectedUniName = universities.find((u) => u.id === selectedUni)?.name;

  return (
    <div className="space-y-6" data-testid="quick-courses-manager">
      <div>
        <h2 className="text-xl font-semibold">Quick Courses Searcher</h2>
        <p className="text-sm text-muted-foreground">
          Manage the course catalogue powering the floating searcher bubble. Import the
          official partner spreadsheet (CSV or XLSX) or add courses manually.
        </p>
      </div>

      {/* University picker */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-1">
          <Label className="text-xs">University *</Label>
          <Select value={selectedUni} onValueChange={setSelectedUni}>
            <SelectTrigger data-testid="qc-university-select">
              <SelectValue placeholder="Choose a university…" />
            </SelectTrigger>
            <SelectContent>
              {universities.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedUni && (
          <>
            <div className="sm:col-span-1">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Course name or partner info…"
                  className="pl-9"
                  data-testid="qc-search-input"
                />
              </div>
            </div>
            <div className="sm:col-span-1 flex items-end gap-2">
              <Button
                onClick={() => setCreating(true)}
                variant="secondary"
                className="flex-1"
                data-testid="qc-add-course-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add Course
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-500 hover:opacity-90 text-white"
                data-testid="qc-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Import CSV / XLSX
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm"
                onChange={(e) => handleImport(e.target.files?.[0])}
                className="hidden"
              />
            </div>
          </>
        )}
      </div>

      {/* Import feedback */}
      {importResult && (
        <div className="p-3 rounded-lg border bg-emerald-50/60 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 flex items-start gap-3" data-testid="qc-import-result">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-emerald-900 dark:text-emerald-100">
              Import complete for {importResult.university_name}
            </p>
            <p className="text-emerald-800 dark:text-emerald-200/80 text-xs">
              {importResult.created} new, {importResult.updated} updated, {importResult.total_rows} rows parsed.
              {importResult.skipped?.length > 0 && ` ${importResult.skipped.length} skipped.`}
            </p>
          </div>
        </div>
      )}

      {/* Course list */}
      {!selectedUni ? (
        <div className="p-12 rounded-lg border-2 border-dashed text-center text-muted-foreground">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Pick a university to view its course catalogue</p>
          <p className="text-xs mt-1">Universities come from Core Settings → Master Data → Universities</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : courses.length === 0 ? (
        <div className="p-12 rounded-lg border-2 border-dashed text-center" data-testid="qc-empty-state">
          <BookMarked className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="font-medium">No courses yet for {selectedUniName}</p>
          <p className="text-xs text-muted-foreground mt-1">Import a spreadsheet above or add courses manually.</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course Name</TableHead>
                <TableHead>Campuses</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {courses.map((c) => (
                <TableRow key={c.id} data-testid={`qc-row-${c.id}`}>
                  <TableCell className="font-medium">{c.course_name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(c.campuses || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        c.campuses.map((cp) => (
                          <Badge
                            key={cp}
                            variant="outline"
                            className="text-[10px] gap-1 border-amber-200 bg-amber-50 text-slate-800"
                          >
                            <MapPin className="w-2.5 h-2.5" />
                            {cp}
                          </Badge>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{c.stage ? <Badge variant="outline">{c.stage}</Badge> : '—'}</TableCell>
                  <TableCell className="text-sm">{c.application_deadline || '—'}</TableCell>
                  <TableCell>
                    {(c.open_closed || '').toLowerCase().includes('clos') ? (
                      <Badge className="bg-amber-500">Closed</Badge>
                    ) : (
                      <Badge className="bg-emerald-600">Open</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setEditing(c)} data-testid={`qc-edit-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(c.id)} data-testid={`qc-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-amber-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/30">
            {courses.length} course{courses.length === 1 ? '' : 's'}
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      <CourseFormDialog
        open={creating || !!editing}
        course={editing}
        universityId={selectedUni}
        universityName={selectedUniName}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setEditing(null); setCreating(false); fetchCourses(); }}
        api={api}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The course will be removed from the searcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-amber-500 hover:bg-slate-800">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import format hint */}
      <div className="p-3 rounded-lg border bg-muted/30 text-xs text-muted-foreground flex items-start gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-medium text-foreground">Accepted import columns:</span>{' '}
          Partner Information, Stage, Course Name, Application Deadline, Open/Closed.
          Sheet names are used as the Partner Information fallback (e.g. <em>Sunderland</em> / <em>London</em>).
          Re-importing the same file <strong>updates</strong> existing rows — duplicates are matched on
          University + Course Name + Stage.
        </div>
      </div>
    </div>
  );
};

const CourseFormDialog = ({ open, course, universityId, universityName, onClose, onSaved, api }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [campusSuggestions, setCampusSuggestions] = useState([]);
  const [campusDraft, setCampusDraft] = useState('');
  const isEdit = !!course;

  useEffect(() => {
    if (!open) return;
    setForm(course || {
      partner_information: '',
      stage: '',
      course_name: '',
      campuses: [],
      application_deadline: '',
      open_closed: 'Open',
      notes: '',
    });
    setCampusDraft('');
    setCampusSuggestions([]); // reset before refetch so stale list doesn't flash
    if (universityId) {
      api().get(`/quick-courses/campuses?university_id=${universityId}`)
        .then((r) => setCampusSuggestions(r.data?.campuses || []))
        .catch(() => setCampusSuggestions([]));
    }
  }, [open, course, universityId, api]);

  const campuses = form.campuses || [];

  const addCampus = (name) => {
    const v = (name || '').trim();
    if (!v) return;
    if (campuses.includes(v)) return;
    setForm({ ...form, campuses: [...campuses, v] });
    setCampusDraft('');
  };

  const removeCampus = (name) => {
    setForm({ ...form, campuses: campuses.filter((c) => c !== name) });
  };

  const save = async () => {
    if (!form.course_name?.trim()) { toast.error('Course name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        partner_information: form.partner_information || null,
        stage: form.stage || null,
        course_name: form.course_name.trim(),
        campuses: campuses,
        application_deadline: form.application_deadline || null,
        open_closed: form.open_closed || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await api().put(`/quick-courses/${course.id}`, payload);
        toast.success('Course updated');
      } else {
        await api().post('/quick-courses', { ...payload, university_id: universityId });
        toast.success('Course added');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const unusedSuggestions = campusSuggestions.filter((s) => !campuses.includes(s));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="qc-course-form">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Course' : 'Add Course'}</DialogTitle>
          <p className="text-xs text-muted-foreground">{universityName}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Course Name *</Label>
            <Input
              value={form.course_name || ''}
              onChange={(e) => setForm({ ...form, course_name: e.target.value })}
              placeholder="e.g., MSc Nursing"
              data-testid="qc-form-course-name"
            />
          </div>

          {/* Campuses multi-select chip input */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Campuses
            </Label>
            <div className="rounded-md border bg-background p-2 space-y-2" data-testid="qc-form-campuses">
              <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                {campuses.length === 0 && (
                  <span className="text-xs text-muted-foreground italic">No campuses yet — add at least one below</span>
                )}
                {campuses.map((c) => (
                  <Badge
                    key={c}
                    variant="outline"
                    className="border-amber-200 bg-amber-50 text-slate-800 gap-1 pr-1"
                    data-testid={`qc-form-campus-chip-${c}`}
                  >
                    <MapPin className="w-2.5 h-2.5" />
                    {c}
                    <button
                      type="button"
                      onClick={() => removeCampus(c)}
                      className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
                      data-testid={`qc-form-campus-remove-${c}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Input
                  value={campusDraft}
                  onChange={(e) => setCampusDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addCampus(campusDraft);
                    }
                  }}
                  placeholder="Type a campus name and press Enter…"
                  className="h-8 text-sm"
                  data-testid="qc-form-campus-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => addCampus(campusDraft)}
                  disabled={!campusDraft.trim()}
                  data-testid="qc-form-campus-add"
                >
                  Add
                </Button>
              </div>
              {unusedSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1 border-t">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground self-center mr-1">Suggestions:</span>
                  {unusedSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => addCampus(s)}
                      className="text-[11px] px-2 py-0.5 rounded-full border bg-muted/40 hover:bg-muted"
                      data-testid={`qc-form-campus-suggestion-${s}`}
                    >
                      + {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Partner Information</Label>
              <Input
                value={form.partner_information || ''}
                onChange={(e) => setForm({ ...form, partner_information: e.target.value })}
                placeholder="e.g., Sunderland (Full Time)"
              />
            </div>
            <div>
              <Label className="text-xs">Stage</Label>
              <Input
                value={form.stage || ''}
                onChange={(e) => setForm({ ...form, stage: e.target.value })}
                placeholder="e.g., Stage 1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Application Deadline</Label>
              <Input
                type="date"
                value={form.application_deadline || ''}
                onChange={(e) => setForm({ ...form, application_deadline: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select
                value={form.open_closed || 'Open'}
                onValueChange={(v) => setForm({ ...form, open_closed: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="qc-form-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {isEdit ? 'Save Changes' : 'Add Course'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickCoursesManager;
